import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage, LANGUAGE_OPTIONS } from '../context/LanguageContext';
import CountrySelect from './CountrySelect';
import DatePicker from './DatePicker';
import TimeZoneSelect from './TimeZoneSelect';
import '../EditProfile.css';

const EditProfile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    first_name_eng: user?.first_name_eng || '',
    last_name_eng: user?.last_name_eng || '',
    nickname: user?.nickname || '',
    gender: user?.gender || '',
    birth_date: user?.birth_date || '',
    weight: user?.weight || '',
    ftp: user?.ftp || '',
    country: user?.country || '',
    language: user?.language || 'en',
    team_id: user?.team?.id || '',
    telegram: user?.social?.telegram || '',
    max: user?.social?.max || '',
    email: user?.social?.email || '',
    time_zone: user?.time_zone || '',
  });

  const [activeTeams, setActiveTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  useEffect(() => {
    fetchActiveTeams();
  }, []);

  const fetchActiveTeams = async () => {
    try {
      setLoadingTeams(true);
      const response = await axios.get('/api/teams/active_teams');
      setActiveTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch active teams:', err);
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        nickname: formData.nickname,
        language: formData.language,
        country: formData.country || '', // Всегда отправляем country, даже если пустой
      };

      if (formData.gender) submitData.gender = formData.gender;
      if (formData.birth_date) submitData.birth_date = formData.birth_date;
      if (formData.weight) submitData.weight = parseFloat(formData.weight);
      if (formData.ftp) {
        submitData.ftp = parseInt(formData.ftp);
      } else if (formData.ftp === '') {
        submitData.ftp = null;
      }
      if (formData.team_id) {
        submitData.team_id = parseInt(formData.team_id);
      } else {
        submitData.team_id = null;
      }

      // Добавляем социальные сети
      if (formData.telegram) submitData.telegram = formData.telegram;
      if (formData.max) submitData.max = formData.max;
      if (formData.email) submitData.email = formData.email;
      
      // Добавляем часовой пояс
      if (formData.time_zone) submitData.time_zone = formData.time_zone;
      
      // Добавляем английские имена
      if (formData.first_name_eng) submitData.first_name_eng = formData.first_name_eng;
      if (formData.last_name_eng) submitData.last_name_eng = formData.last_name_eng;

      const response = await axios.put('/api/users/profile', submitData);
      updateUser(response.data.user);
      setSuccess(t('profile.updateSuccess'));
      
      setTimeout(() => {
        navigate('/profile');
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="edit-profile-container">
      <div className="edit-profile-header">
        <h1>{t('profile.editProfile')}</h1>
        <p>{t('profile.editDescription')}</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form onSubmit={handleSubmit} className="edit-profile-form">
        {(!user?.social?.email || user?.social?.email === 'nil') && (
          <div className="warning-message" style={{
            backgroundColor: 'var(--c-fef3cd-bg)',
            border: '1px solid var(--c-ffeaa7-bd)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'flex-start'
          }}>
            <svg 
              style={{ flexShrink: 0, marginRight: '12px', marginTop: '2px' }}
              width="20" 
              height="20" 
              viewBox="0 0 20 20" 
              fill="#f59e0b"
            >
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 style={{ margin: '0 0 8px 0', color: 'var(--c-92400e-fg)', fontSize: '15px', fontWeight: '600' }}>
                {t('profile.emailRequiredTitle')}
              </h4>
              <p style={{ margin: 0, color: 'var(--c-78350f-fg)', fontSize: '14px', lineHeight: '1.5' }}>
                {t('profile.emailRequiredText')}
              </p>
            </div>
          </div>
        )}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name">{t('signup.firstName')} {t('signup.required')}</label>
            <input
              id="first_name"
              name="first_name"
              type="text"
              value={formData.first_name}
              onChange={handleChange}
              placeholder={t('signup.firstNamePlaceholder')}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_name">{t('signup.lastName')} {t('signup.required')}</label>
            <input
              id="last_name"
              name="last_name"
              type="text"
              value={formData.last_name}
              onChange={handleChange}
              placeholder={t('signup.lastNamePlaceholder')}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name_eng">{t('signup.firstNameEng')}</label>
            <input
              id="first_name_eng"
              name="first_name_eng"
              type="text"
              value={formData.first_name_eng}
              onChange={handleChange}
              placeholder={t('signup.firstNameEngPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_name_eng">{t('signup.lastNameEng')}</label>
            <input
              id="last_name_eng"
              name="last_name_eng"
              type="text"
              value={formData.last_name_eng}
              onChange={handleChange}
              placeholder={t('signup.lastNameEngPlaceholder')}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="nickname">{t('signup.nickname')} {t('signup.required')}</label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            value={formData.nickname}
            onChange={handleChange}
            placeholder={t('signup.nicknamePlaceholder')}
            required
            minLength="3"
            maxLength="50"
          />
          <small style={{ color: 'var(--c-718096-fg)', display: 'block', marginTop: '5px' }}>
            ⚠️ {t('signup.nicknameHint')}
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="email">{t('signup.email')} {t('signup.required')}</label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t('signup.emailPlaceholder')}
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="gender">{t('signup.gender')}</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <option value="">{t('signup.selectGender')}</option>
              <option value="male">{t('signup.male')}</option>
              <option value="female">{t('signup.female')}</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="birth_date">{t('signup.birthDate')}</label>
            <DatePicker
              value={formData.birth_date}
              onChange={(value) => setFormData(prev => ({ ...prev, birth_date: value }))}
              placeholder={t('signup.birthDatePlaceholder')}
              language={language}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="weight">{t('signup.weight')}</label>
            <input
              id="weight"
              name="weight"
              type="number"
              step="0.1"
              value={formData.weight}
              onChange={handleChange}
              placeholder={t('signup.weightPlaceholder')}
              min="1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="ftp">{t('signup.ftp')}</label>
            <input
              id="ftp"
              name="ftp"
              type="number"
              step="1"
              value={formData.ftp}
              onChange={handleChange}
              placeholder={t('signup.ftpPlaceholder')}
              min="1"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="country">{t('signup.country')}</label>
            <CountrySelect
              value={formData.country}
              onChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
              language={language}
            />
          </div>

          <div className="form-group">
            <label htmlFor="time_zone">{t('signup.timeZone')}</label>
            <TimeZoneSelect
              value={formData.time_zone}
              onChange={(value) => setFormData(prev => ({ ...prev, time_zone: value }))}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="language">{t('signup.language')}</label>
            <select
              id="language"
              name="language"
              value={formData.language}
              onChange={handleChange}
            >
              {LANGUAGE_OPTIONS.map(({ code, name }) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="team_id">{t('profile.team')}</label>
            <select
              id="team_id"
              name="team_id"
              value={formData.team_id}
              onChange={handleChange}
              disabled={loadingTeams}
            >
              <option value="">{t('profile.noTeam')}</option>
              {activeTeams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-section-title">
          <h3>{t('profile.socialNetworks')}</h3>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="telegram">
              <span className="social-label">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#0088cc" style={{marginRight: '8px'}}>
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                </svg>
                Telegram
              </span>
            </label>
            <input
              id="telegram"
              name="telegram"
              type="text"
              value={formData.telegram}
              onChange={handleChange}
              placeholder="@username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="max">
              <span className="social-label">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 1000 1000" style={{marginRight: '8px'}}>
                  <defs><linearGradient id="b"><stop offset="0" stop-color="#00f"/><stop offset="1" stop-opacity="0"/></linearGradient><linearGradient id="a"><stop offset="0" stop-color="#4cf"/><stop offset=".662" stop-color="#53e"/><stop offset="1" stop-color="#93d"/></linearGradient><linearGradient id="c" x1="117.847" x2="1000" y1="760.536" y2="500" gradientUnits="userSpaceOnUse" href="#a"/><radialGradient id="d" cx="-87.392" cy="1166.116" r="500" fx="-87.392" fy="1166.116" gradientTransform="rotate(51.356 1551.478 559.3)scale(2.42703433 1)" gradientUnits="userSpaceOnUse" href="#b"/></defs><rect width="1000" height="1000" fill="url(#c)" ry="249.681"/><rect width="1000" height="1000" fill="url(#d)" ry="249.681"/>
                  <path fill="#fff" fill-rule="evenodd" d="M508.211 878.328c-75.007 0-109.864-10.95-170.453-54.75-38.325 49.275-159.686 87.783-164.979 21.9 0-49.456-10.95-91.248-23.36-136.873-14.782-56.21-31.572-118.807-31.572-209.508 0-216.626 177.754-379.597 388.357-379.597 210.785 0 375.947 171.001 375.947 381.604.707 207.346-166.595 376.118-373.94 377.224m3.103-571.585c-102.564-5.292-182.499 65.7-200.201 177.024-14.6 92.162 11.315 204.398 33.397 210.238 10.585 2.555 37.23-18.98 53.837-35.587a189.8 189.8 0 0 0 92.71 33.032c106.273 5.112 197.08-75.794 204.215-181.95 4.154-106.382-77.67-196.486-183.958-202.574Z" clip-rule="evenodd"/>
                </svg>
                Max
              </span>
            </label>
            <input
              id="max"
              name="max"
              type="text"
              value={formData.max}
              onChange={handleChange}
              placeholder="Max ID"
            />
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('profile.cancel')}
          </button>
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading}
          >
            {loading ? t('profile.saving') : t('profile.saveChanges')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProfile;